'use client';

import * as React from 'react';
import Image from 'next/image';
import Link from 'next/link';
import {
  ChevronRightIcon,
  GlobeIcon,
  WalletIcon,
  CalendarIcon,
  CalendarDaysIcon
} from 'lucide-react';
import { motion } from 'motion/react';

import { routes } from '@workspace/routes';
import { Badge } from '@workspace/ui/components/badge';
import { buttonVariants } from '@workspace/ui/components/button';
import { ScrollArea, ScrollBar } from '@workspace/ui/components/scroll-area';
import { Separator } from '@workspace/ui/components/separator';
import {
  UnderlinedTabs,
  UnderlinedTabsContent,
  UnderlinedTabsList,
  UnderlinedTabsTrigger
} from '@workspace/ui/components/tabs';
import { cn } from '@workspace/ui/lib/utils';

import { useHeroTab } from '~/contexts/hero-tab-context';

function HeroPill(): React.JSX.Element {
  return (
    <motion.div
      initial={{ filter: 'blur(10px)', opacity: 0, y: -20 }}
      animate={{ filter: 'blur(0px)', opacity: 1, y: 0 }}
      transition={{ duration: 0.8 }}
      className="flex items-center justify-center"
    >
      <Link href={routes.dashboard.auth.SignUp}>
        <Badge
          variant="outline"
          className="group h-8 rounded-full border-white/20 bg-black/20 px-3 text-xs font-medium text-white shadow-xs backdrop-blur-md duration-200 hover:bg-black/40 sm:text-sm"
        >
          <div className="w-fit py-0.5 text-center text-xs text-white sm:text-sm">
            New!
          </div>
          <Separator
            orientation="vertical"
            className="mx-2 bg-white/30"
          />
          Create your wedding website in minutes!
          <ChevronRightIcon className="ml-1.5 size-3 shrink-0 text-white transition-transform group-hover:translate-x-0.5" />
        </Badge>
      </Link>
    </motion.div>
  );
}

function HeroTitle(): React.JSX.Element {
  return (
    <motion.div
      initial={{ filter: 'blur(10px)', opacity: 0, y: 20 }}
      animate={{ filter: 'blur(0px)', opacity: 1, y: 0 }}
      transition={{ delay: 0.2, duration: 0.4 }}
    >
      <h1 className="mt-6 text-center text-[48px] font-bold leading-[54px] tracking-[-1.2px] text-white [font-kerning:none] sm:text-[56px] md:text-[64px] lg:text-[76px] lg:leading-[74px] lg:tracking-[-2px]">
        Your wedding,
        <br /> planned perfectly.
      </h1>
    </motion.div>
  );
}

function HeroDescription(): React.JSX.Element {
  return (
    <motion.p
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.4, duration: 0.4 }}
      className="mx-auto mt-3 max-w-[560px] text-balance text-center text-lg leading-[26px] text-white/90 sm:text-xl lg:mt-6"
    >
      Guest lists, budgets, timelines, your website—everything in one place, so nothing slips through the cracks.
    </motion.p>
  );
}

function HeroButtons(): React.JSX.Element {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.6, duration: 0.4 }}
      className="mx-auto flex w-full flex-col gap-2 px-7 sm:w-auto sm:flex-row sm:px-0"
    >
      <Link
        href={routes.dashboard.auth.SignUp}
        className={cn(
          buttonVariants({
            variant: 'default'
          }),
          'h-10 rounded-xl bg-white text-black hover:bg-white/90 sm:h-9'
        )}
      >
        Start planning
      </Link>
      <Link
        href={routes.marketing.Contact}
        className={cn(
          buttonVariants({
            variant: 'outline'
          }),
          'h-10 rounded-xl border-white/20 bg-black/20 text-white hover:bg-black/40 hover:text-white sm:h-9'
        )}
      >
        Learn more
      </Link>
    </motion.div>
  );
}

function HeroIllustration(): React.JSX.Element {
  const { activeTab, setActiveTab } = useHeroTab();

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 0.6, duration: 0.4 }}
      className="relative mt-3 lg:mt-6"
    >
      <UnderlinedTabs value={activeTab} onValueChange={setActiveTab}>
        <ScrollArea className="max-w-screen lg:max-w-none">
          <UnderlinedTabsList className="relative z-20 mb-6 flex h-fit flex-row flex-wrap justify-center md:flex-nowrap">
            <UnderlinedTabsTrigger
              value="feature1"
              className="mx-1 px-2.5 text-white data-[state=active]:text-[#d1def9] data-[state=active]:border-[#d1def9] sm:mx-2 sm:px-3"
            >
              <GlobeIcon className="mr-2 size-4 shrink-0" />
              Website
            </UnderlinedTabsTrigger>
            <UnderlinedTabsTrigger
              value="feature2"
              className="mx-1 px-2.5 text-white data-[state=active]:text-[#d1def9] data-[state=active]:border-[#d1def9] sm:mx-2 sm:px-3"
            >
              <WalletIcon className="mr-2 size-4 shrink-0" />
              Budget
            </UnderlinedTabsTrigger>
            <UnderlinedTabsTrigger
              value="feature3"
              className="mx-1 px-2.5 text-white data-[state=active]:text-[#d1def9] data-[state=active]:border-[#d1def9] sm:mx-2 sm:px-3"
            >
              <CalendarIcon className="mr-2 size-4 shrink-0" />
              Events
            </UnderlinedTabsTrigger>
            <UnderlinedTabsTrigger
              value="feature4"
              className="mx-1 px-2.5 text-white data-[state=active]:text-[#d1def9] data-[state=active]:border-[#d1def9] sm:mx-2 sm:px-3"
            >
              <CalendarDaysIcon className="mr-2 size-4 shrink-0" />
              Timeline
            </UnderlinedTabsTrigger>
          </UnderlinedTabsList>
          <ScrollBar
            orientation="horizontal"
            className="invisible"
          />
        </ScrollArea>
        <div className="relative mb-1 w-full px-4 sm:px-8 md:px-12 lg:px-16">
          <div className="relative z-20 mx-auto max-w-6xl">
            <UnderlinedTabsContent value="feature1">
              <Image
                priority
                quality={100}
                src="/landing-page/Website.png"
                width="1400"
                height="850"
                alt="Wedding website builder"
                className="w-full rounded-xl border shadow-2xl"
              />
            </UnderlinedTabsContent>
            <UnderlinedTabsContent value="feature2">
              <Image
                quality={100}
                src="/landing-page/Budget.png"
                width="1400"
                height="850"
                alt="Wedding budget tracker"
                className="w-full rounded-xl border shadow-2xl"
              />
            </UnderlinedTabsContent>
            <UnderlinedTabsContent value="feature3">
              <Image
                quality={100}
                src="/landing-page/Events.png"
                width="1400"
                height="850"
                alt="Wedding events planner"
                className="w-full rounded-xl border shadow-2xl"
              />
            </UnderlinedTabsContent>
            <UnderlinedTabsContent value="feature4">
              <Image
                quality={100}
                src="/landing-page/Timeline.png"
                width="1400"
                height="850"
                alt="Wedding planning timeline"
                className="w-full rounded-xl border shadow-2xl"
              />
            </UnderlinedTabsContent>
          </div>
        </div>
      </UnderlinedTabs>
    </motion.div>
  );
}

export function Hero(): React.JSX.Element {
  return (
    <section className="relative min-h-[80vh] w-full overflow-hidden bg-black">
      <div className="absolute inset-0 z-0">
        <Image
          src="/assets/unsplash/jeremy-wong-weddings-464ps_nOflw-unsplash.jpg"
          alt="Wedding background"
          fill
          className="object-cover object-center"
          priority
        />
        <div className="absolute inset-0 bg-black/40" />
      </div>
      
      <div className="relative z-10 mx-auto flex min-h-[80vh] flex-col justify-center gap-6 px-2 py-20 sm:px-4 md:py-32 lg:py-40">
        <div className="gap-2">
          <HeroPill />
          <HeroTitle />
        </div>
        <HeroDescription />
        <HeroButtons />
        <HeroIllustration />
      </div>
    </section>
  );
}
